import * as React from 'react';
import {withRouter} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import type {
    GetSearchResultsByType_search_quickResults_accountResults as AccountResult,
    GetSearchResultsByType_search_quickResults_contactResults as ContactResult,
    GetSearchResultsByType_search_quickResults_leadResults as LeadResult,
} from 'nutshell-graphql-types';

import {SearchSection} from './search-section';

import styles from './search-bar-popover.module.css';

type OwnProps = {
    showSearchAll: boolean;
    accountResults: Array<AccountResult>;
    contactResults: Array<ContactResult>;
    leadResults: Array<LeadResult>;
    setIsSearchBarFocused: (isSearchBarFocused: boolean) => void;
    setSearchValue: (searchValue: string) => void;
    searchValue: string;
};
type Props = OwnProps & ContextRouter;

function SearchSectionsComponent(props: Props) {
    const searchResultsRef = React.useRef(null);

    React.useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!searchResultsRef.current) return;

            // Skip handling if the event target is an input or the search bar
            if (
                e.target instanceof HTMLInputElement ||
                (e.target instanceof HTMLElement && e.target.classList.contains('nut-search-bar'))
            ) {
                return;
            }

            // @ts-expect-error
            const focusableElements = searchResultsRef.current.querySelectorAll('a[href], button');
            const elementsArray = Array.from(focusableElements);
            const currentFocusIndex = elementsArray.indexOf(document.activeElement);

            // Calculate section sizes (including "Search all" link if present)
            const accountSize = props.accountResults.length + (props.showSearchAll ? 1 : 0);
            const contactSize = props.contactResults.length + (props.showSearchAll ? 1 : 0);
            const leadSize = props.leadResults.length + (props.showSearchAll ? 1 : 0);

            switch (e.key) {
                case 'ArrowDown':
                case 'Down':
                case '40': {
                    const nextIndex =
                        currentFocusIndex < focusableElements.length - 1
                            ? currentFocusIndex + 1
                            : 0;
                    e.preventDefault();
                    focusableElements[nextIndex].focus();
                    break;
                }
                case 'ArrowUp':
                case 'Up':
                case '38': {
                    const prevIndex =
                        currentFocusIndex > 0
                            ? currentFocusIndex - 1
                            : focusableElements.length - 1;
                    e.preventDefault();
                    focusableElements[prevIndex].focus();
                    break;
                }
                case 'ArrowRight':
                case 'Right':
                case '39': {
                    e.preventDefault();
                    // If in accounts section, jump to contacts
                    if (currentFocusIndex < accountSize) {
                        const relativePosition = currentFocusIndex % accountSize;
                        const targetIndex =
                            accountSize + Math.min(relativePosition, contactSize - 1);
                        focusableElements[targetIndex].focus();
                    }
                    // If in contacts section, jump to leads
                    else if (currentFocusIndex < accountSize + contactSize) {
                        const relativePosition = (currentFocusIndex - accountSize) % contactSize;
                        const targetIndex =
                            accountSize + contactSize + Math.min(relativePosition, leadSize - 1);
                        focusableElements[targetIndex].focus();
                    }
                    // If in leads section, wrap around to accounts
                    else {
                        const relativePosition =
                            (currentFocusIndex - (accountSize + contactSize)) % leadSize;
                        const targetIndex = Math.min(relativePosition, accountSize - 1);
                        focusableElements[targetIndex].focus();
                    }
                    break;
                }
                case 'ArrowLeft':
                case 'Left':
                case '37': {
                    e.preventDefault();
                    // If in leads section, jump to contacts
                    if (currentFocusIndex >= accountSize + contactSize) {
                        const relativePosition =
                            (currentFocusIndex - (accountSize + contactSize)) % leadSize;
                        const targetIndex =
                            accountSize + Math.min(relativePosition, contactSize - 1);
                        focusableElements[targetIndex].focus();
                    }
                    // If in contacts section, jump to accounts
                    else if (currentFocusIndex >= accountSize) {
                        const relativePosition = (currentFocusIndex - accountSize) % contactSize;
                        const targetIndex = Math.min(relativePosition, accountSize - 1);
                        focusableElements[targetIndex].focus();
                    }
                    // If in accounts section, wrap around to leads
                    else {
                        const relativePosition = currentFocusIndex % accountSize;
                        const targetIndex =
                            accountSize + contactSize + Math.min(relativePosition, leadSize - 1);
                        focusableElements[targetIndex].focus();
                    }
                    break;
                }
                case 'Enter':
                case '13': {
                    const focusedElement = document.activeElement;
                    e.preventDefault();
                    if (focusedElement instanceof HTMLAnchorElement) {
                        window.location.href = focusedElement.href;
                        props.setIsSearchBarFocused(false);
                        props.setSearchValue('');
                    }
                    break;
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [props]);

    const {history, setIsSearchBarFocused, setSearchValue} = props;
    React.useEffect(() => {
        // Once the user navigates to a search result, close the popover and
        // clear the search bar
        const unlisten = history.listen(() => {
            setIsSearchBarFocused(false);
            setSearchValue('');
        });

        return unlisten;
    }, [history, setIsSearchBarFocused, setSearchValue]);

    return (
        <div ref={searchResultsRef} className={styles['search-results-grid']}>
            <SearchSection
                title='COMPANIES'
                results={props.accountResults}
                showSearchAll={props.showSearchAll}
                searchAllPath={`/companies?q=${props.searchValue}`}
            />
            <SearchSection
                title='PEOPLE'
                results={props.contactResults}
                showSearchAll={props.showSearchAll}
                searchAllPath={`/people?q=${props.searchValue}`}
            />
            <SearchSection
                title='LEADS'
                results={props.leadResults}
                showSearchAll={props.showSearchAll}
                searchAllPath={`/leads?q=${props.searchValue}`}
            />
        </div>
    );
}

// @ts-expect-error
export const SearchSections = withRouter<OwnProps>(SearchSectionsComponent);
